import { AsyncSelect, useAsyncLookup } from '@/components';
import { useEffect, useRef, useState } from 'react';
import { useHomeLookupSource } from '../Homes.Common';
import { useTranslation } from 'react-i18next';

interface Props {
  communityId: string;
  projectId?: string;
  className?: string;
  onSelected: (id: string | undefined) => void;
  selectedId: string | undefined;
  showResidentNames?: boolean;
  homeId: string | undefined;
  setHomeId: (id: string | undefined) => void;
}

export const PROJECT_HOME_STORAGE_KEY = 'selectedProjectHomeId';

interface ProjectHomeSelectionOptions {
  preferInitialId?: boolean;
}

export function useProjectHomeSelectedState(
  initialId?: string,
  options?: ProjectHomeSelectionOptions,
): Pick<Props, 'onSelected' | 'selectedId' | 'homeId' | 'setHomeId'> {
  const preferInitialId = options?.preferInitialId ?? false;
  const [selectedId, setSelectedId] = useState<string | undefined>(() => {
    const stored = sessionStorage.getItem(PROJECT_HOME_STORAGE_KEY);
    return preferInitialId
      ? initialId ?? stored ?? undefined
      : stored ?? initialId;
  });

  const [homeId, setHomeId] = useState<string | undefined>();
  const initialIdRef = useRef(initialId);

  useEffect(() => {
    if (preferInitialId && initialId !== initialIdRef.current) {
      initialIdRef.current = initialId;
      if (!initialId) return;

      setSelectedId(initialId);
    }
  }, [initialId, preferInitialId]);

  useEffect(() => {
    if (selectedId == null) {
      sessionStorage.removeItem(PROJECT_HOME_STORAGE_KEY);
    } else {
      sessionStorage.setItem(PROJECT_HOME_STORAGE_KEY, selectedId);
    }
  }, [selectedId]);

  return { selectedId, onSelected: setSelectedId, homeId, setHomeId };
}

export function SearchProjectHomeInput(props: Props) {
  const {
    communityId,
    projectId,
    className,
    onSelected,
    selectedId,
    showResidentNames,
    setHomeId,
  } = props;
  const dataSource = useHomeLookupSource(
    communityId,
    projectId,
    showResidentNames,
  );
  const { t } = useTranslation();

  const { data, isLoading } = useAsyncLookup(dataSource, {
    autoLoad: true,
  });

  const userInteractedRef = useRef(false);

  const handleChange = (value: string | undefined) => {
    userInteractedRef.current = true;
    onSelected(value);
  };

  useEffect(() => {
    if (data && !isLoading && !selectedId && !userInteractedRef.current) {
      const sorted = [...data].sort(
        (a, b) => (a.number ?? 0) - (b.number ?? 0),
      );
      onSelected(sorted[0]?.value);
    }
  }, [data, isLoading, onSelected, selectedId]);

  useEffect(() => {
    if (selectedId) {
      const exists = data.find((x) => x.value === selectedId);
      exists && setHomeId && setHomeId(selectedId);
    } else setHomeId(undefined);
  }, [data, selectedId, setHomeId]);

  return (
    <AsyncSelect
      className={className}
      value={selectedId}
      onChange={handleChange}
      dataSource={dataSource}
      placeholder={t('developScope.selectHome')}
      preload
      placement="bottomLeft"
      onSearchLoad={(options) => {
        !options.some((x) => x.value === selectedId) && onSelected(undefined);
      }}
    />
  );
}
